import React, { useState } from 'react';
import MarketingHeader from '../../components/MarketingHeader';
import {
  useCreateSocialMediaAsset,
  useDeleteSocialMediaAsset,
  useSocialMediaAssets,
} from '../../hooks/useSocial';
import { supabase } from '../../services/supabase';
import { DangerButton, PrimaryButton } from '../../styles/marketingSharedStyles';
import { Card, CardTitle, Item, List, Page, Row, Subtle } from './socialStyles';

const SocialMediaLibraryPage: React.FC = () => {
  const assets = useSocialMediaAssets();
  const createAsset = useCreateSocialMediaAsset();
  const deleteAsset = useDeleteSocialMediaAsset();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onUpload = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
      const key = `${Date.now()}-${crypto.randomUUID()}${ext}`;
      const path = `uploads/${key}`;
      const { error: uploadError } = await supabase.storage
        .from('social-media-assets')
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) throw uploadError;

      await createAsset.mutateAsync({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        fileSizeBytes: file.size,
        storagePath: path,
      });
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Page>
      <MarketingHeader activeTab="social" />
      <Card>
        <CardTitle>Media Library</CardTitle>
        <Row>
          <input
            type="file"
            accept="image/*,video/*"
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              void onUpload(file);
            }}
          />
          <PrimaryButton type="button" disabled={uploading}>
            {uploading ? 'Uploading...' : 'Choose File'}
          </PrimaryButton>
        </Row>
        {error ? <Subtle>{error}</Subtle> : null}
      </Card>

      <Card>
        <CardTitle>Uploaded Assets</CardTitle>
        {assets.isLoading ? (
          <Subtle>Loading assets...</Subtle>
        ) : (
          <List>
            {(assets.data ?? []).map((asset) => (
              <Item key={asset.id}>
                <Row>
                  <strong>{asset.file_name}</strong>
                  <span>{asset.mime_type}</span>
                  <span>{asset.file_size_bytes ? `${Math.ceil(asset.file_size_bytes / 1024)} KB` : '-'}</span>
                  <span>{asset.storage_path}</span>
                  <DangerButton type="button" onClick={() => deleteAsset.mutate(asset.id)} disabled={deleteAsset.isPending}>
                    Remove
                  </DangerButton>
                </Row>
              </Item>
            ))}
            {(assets.data ?? []).length === 0 ? <Subtle>No media uploaded yet.</Subtle> : null}
          </List>
        )}
      </Card>
    </Page>
  );
};

export default SocialMediaLibraryPage;
